import Add from "@mui/icons-material/Add";
import { Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { Dialog } from "components/Dialog";
import { CellText } from "components/Omnisearch/helpComponents/CellText";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { DueDateCell } from "components/Submission/DueDateCell";
import { FacilitiesCell } from "components/Submission/FacilitiesCell";
import {
  ALL_STATUSES,
  STATUS_FILTER_OPTIONS,
  US_STATES,
} from "components/Submission/helpers/constants";
import { SubmissionStatusCell } from "components/Submission/SubmissionStatusCell";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  AssociatedFacilitiesQuery,
  AssociatedFacilitiesQueryVariables,
  AssociatedTierIiReportsQuery,
  AssociatedTierIiReportsQueryVariables,
  AssociatedSubmissionsQuery,
  AssociatedSubmissionsQueryVariables,
  SubmissionStatus,
} from "generated-graphql/graphql";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { usePartnerTenant } from "hooks/usePartnerTenant";
import { useMemo } from "react";
import { STATUS_LABELS } from "utils/submissionStatusLabels";

import {
  FACILITY_ASSOCIATIONS_QUERY,
  TIER_II_REPORT_ASSOCIATIONS_QUERY,
  SUBMISSION_ASSOCIATIONS_QUERY,
} from "./lib/schema";
import { Association } from "./lib/types";

export const AddAssociation = ({
  onClose,
  addAssociation,
  disabledFacilityIds,
  disabledReportIds,
  disabledSubmissionIds,
  tenantId,
  type,
}: {
  onClose: () => void;
  addAssociation: (association: Association) => void;
  disabledFacilityIds: string[];
  disabledReportIds: string[];
  disabledSubmissionIds: string[];
  tenantId: string;
  type: "Report" | "Facility" | "Submission";
}) => {
  const { partnerTenantId } = usePartnerTenant(tenantId);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={true}
      onClose={onClose}
      title={`Add ${type} Association`}
      noPrimaryAction
    >
      {type === "Report" && (
        <AddReportDataGrid
          tenantId={tenantId}
          partnerTenantId={partnerTenantId}
          disabledReportIds={disabledReportIds}
          add={(reportId) => {
            addAssociation({ reportId, amount: 0 });
            onClose();
          }}
        />
      )}
      {type === "Facility" && (
        <AddFacilityDataGrid
          tenantId={tenantId}
          partnerTenantId={partnerTenantId}
          disabledFacilityIds={disabledFacilityIds}
          add={(facilityId) => {
            addAssociation({ facilityId, amount: 0 });
            onClose();
          }}
        />
      )}
      {type === "Submission" && (
        <AddSubmissionDataGrid
          tenantId={tenantId}
          disabledSubmissionIds={disabledSubmissionIds}
          add={(submissionId) => {
            addAssociation({ submissionId, amount: 0 });
            onClose();
          }}
        />
      )}
    </Dialog>
  );
};

const AddReportDataGrid = ({
  tenantId,
  partnerTenantId,
  disabledReportIds,
  add,
}: {
  tenantId: string;
  partnerTenantId: string | null;
  disabledReportIds: string[];
  add: (tierIIReportId: string) => void;
}) => {
  const tenantFilter = partnerTenantId
    ? `partnerTenantId:${partnerTenantId}`
    : `tenantId:${tenantId}`;
  return (
    <OmnisearchDataGrid<
      AssociatedTierIiReportsQuery,
      AssociatedTierIiReportsQueryVariables,
      AssociatedTierIiReportsQuery["tierIIReports"]["items"][number]
    >
      persistenceKey="add-report"
      isURLDriven={false}
      tenantId={tenantId}
      columns={[
        {
          field: "facility",
          headerName: "Facility",
          filterKeyType: "facility",
          flex: 1,
          valueGetter: (_, row) => row.facility.name,
        },
        {
          headerName: "Facility Address",
          field: "facilityAddress",
          sortable: false,
          flex: 1.2,
          valueGetter: (_, row) => {
            const address = row.facility.streetAddress1;
            if (address) {
              const { streetAddress1, city, state, zip } = row.facility;
              return `${streetAddress1}, ${city}, ${state} ${zip}`;
            }
            return "";
          },
        },
        { field: "reportingYear", headerName: "Reporting Year", flex: 0.4 },
        { field: "reportKind", headerName: "Type", flex: 0.4 },
        {
          field: "createdAt",
          headerName: "Date Opened",
          flex: 0.6,
          valueGetter: (_, row) => {
            return row.createdAt;
          },
          valueFormatter: (value) => {
            if (!value) return "";
            const date = new Date(value);
            return date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
          },
        },
        {
          field: "encampStatus",
          headerName: "Status",
          flex: 0.7,
          valueGetter: (_, row) => {
            return row.encampStatus;
          },
          valueFormatter: (value) => {
            return prettyPrintEnumValue(value);
          },
        },
        {
          field: "actions",
          type: "actions",
          getActions: (params) => {
            const actions = [];

            const isAddReportDisabled = disabledReportIds.includes(params.row.id);
            actions.push(
              <GridActionsCellItem
                // sx={{
                //   "&.Mui-disabled": {
                //     pointerEvents: "all",
                //   },
                // }}
                icon={
                  <Tooltip title={isAddReportDisabled ? "Report already added" : "Add Report"}>
                    <Add />
                  </Tooltip>
                }
                label="Add Report"
                disabled={isAddReportDisabled}
                onClick={() => {
                  if (params.row.id) {
                    add(params.row.id);
                  }
                }}
              />
            );

            return actions;
          },
          flex: 0.1,
        },
      ]}
      dataQuery={TIER_II_REPORT_ASSOCIATIONS_QUERY}
      defaultSearch={`${tenantFilter} reportingYear:${currentTierIIReportingYear}`}
      getItems={(data) => data.tierIIReports.items}
      getCount={(data) => data.tierIIReports.count}
      withPadding={false}
    />
  );
};

const AddFacilityDataGrid = ({
  tenantId,
  partnerTenantId,
  disabledFacilityIds,
  add,
}: {
  tenantId: string;
  partnerTenantId: string | null;
  disabledFacilityIds: string[];
  add: (facilityId: string) => void;
}) => {
  const tenantFilter = partnerTenantId
    ? `partnerTenantId:${partnerTenantId}`
    : `tenantId:${tenantId}`;
  return (
    <OmnisearchDataGrid<
      AssociatedFacilitiesQuery,
      AssociatedFacilitiesQueryVariables,
      AssociatedFacilitiesQuery["facilities"]["items"][number]
    >
      tenantId={tenantId}
      persistenceKey="add-facility"
      isURLDriven={false}
      columns={[
        { field: "name", headerName: "Facility", flex: 1 },
        {
          field: "address",
          headerName: "Address",
          renderCell: (params) => {
            if (params.row.streetAddress1) {
              const { state, city, zip, streetAddress1, streetAddress2 } = params.row;
              return `${streetAddress1} ${streetAddress2} ${city}, ${state} ${zip}`;
            }
            return null;
          },
          sortable: false,
        },
        {
          field: "actions",
          type: "actions",
          getActions: (params) => {
            const actions = [];

            const isAddFacilityDisabled = disabledFacilityIds.includes(params.row.id);

            actions.push(
              <GridActionsCellItem
                // sx={{
                //   "&.Mui-disabled": {
                //     pointerEvents: "all",
                //   },
                // }}
                icon={
                  <Tooltip
                    title={isAddFacilityDisabled ? "Facility already added" : "Add Facility"}
                  >
                    <Add />
                  </Tooltip>
                }
                label="Add Facility"
                disabled={isAddFacilityDisabled}
                onClick={() => {
                  add(params.row.id);
                }}
              />
            );

            return actions;
          },
        },
      ]}
      dataQuery={FACILITY_ASSOCIATIONS_QUERY}
      defaultSearch={tenantFilter}
      getItems={(data) => data.facilities.items}
      getCount={(data) => data.facilities.count}
      withPadding={false}
    />
  );
};

const AddSubmissionDataGrid = ({
  tenantId,
  disabledSubmissionIds,
  add,
}: {
  tenantId: string;
  disabledSubmissionIds: string[];
  add: (submissionId: string) => void;
}) => {
  const additionalFilterColumns = useMemo<FilterKey[]>(
    () => [
      {
        header: "Submission Type",
        key: "submissionType",
        filterKeyType: "text",
      },
      {
        header: "Status",
        key: "submissionStatus",
        filterKeyType: "enumAutocomplete",
        enumValues: ALL_STATUSES,
        enumPresentationFunction: (value: string) =>
          STATUS_FILTER_OPTIONS[value as SubmissionStatus] || value,
      },

      {
        header: "Facility",
        key: "facilityId",
        filterKeyType: "multipleFacility",
      },
      {
        header: "State",
        key: "states",
        filterKeyType: "enumAutocomplete",
        enumValues: US_STATES,
        enumPresentationFunction: (value: string) =>
          STATE_NAMES[value as keyof typeof STATE_NAMES] || value,
      },
      {
        header: "Due Date",
        key: "dueDate",
        filterKeyType: "date",
        dateRangeShortcuts: true,
      },
    ],
    []
  );
  const excludeFilterColumns = useMemo(
    () => [
      "submissionType",
      "dueDate",
      "status",
      "facility",
      "deadline",
      "facilities",
      "submission.status",
    ],
    []
  );
  const tenantFilter = `tenantId:${tenantId}`;
  return (
    <OmnisearchDataGrid<
      AssociatedSubmissionsQuery,
      AssociatedSubmissionsQueryVariables,
      AssociatedSubmissionsQuery["submissionVirtualEvents"]["items"][number]
    >
      tenantId={tenantId}
      persistenceKey="add-submission"
      isURLDriven={false}
      columns={[
        {
          field: "submissionType",
          headerName: "Submission Type",
          flex: 0.8,
          filterKeyType: "text",
          renderCell: ({ row: { title } }) => {
            return (
              <CellText variant="body2" noWrap>
                {/* For SYSTEM submissions, this is the pretty printed submission type
              For user submissions, this is the task title which is meant to be in sync with the submission title*/}
                {title}
              </CellText>
            );
          },
        },
        {
          field: "submission.status",
          headerName: "Status",
          sortable: true,
          flex: 0.6,
          renderCell: ({ row }) => <SubmissionStatusCell status={row.submission?.status} />,
        },
        {
          field: "facility",
          headerName: "Facility",
          flex: 1,
          sortable: false,
          renderCell({ row }) {
            const facilities = row.facilities ?? [];
            return <FacilitiesCell facilities={facilities} />;
          },
        },
        {
          field: "dueDate",
          headerName: "Due Date",
          flex: 0.6,
          filterKeyType: "date",
          dateRangeShortcuts: true,
          renderCell: ({ row }) => {
            return <DueDateCell dueDate={row.submission?.dueDate} deadline={row.deadline} />;
          },
        },
        {
          field: "actions",
          type: "actions",
          getActions: (params) => {
            const actions = [];
            // Use submission.id if available (materialized submission), otherwise skip
            const submissionId = params.row.submission?.id;
            const isAddSubmissionDisabled =
              !submissionId || disabledSubmissionIds.includes(submissionId);

            actions.push(
              <GridActionsCellItem
                icon={
                  <Tooltip
                    title={
                      !submissionId
                        ? "No submission (virtual event)"
                        : isAddSubmissionDisabled
                          ? "Submission already added"
                          : "Add Submission"
                    }
                  >
                    <Add />
                  </Tooltip>
                }
                label="Add Submission"
                disabled={isAddSubmissionDisabled}
                onClick={() => {
                  if (submissionId) {
                    add(submissionId);
                  }
                }}
              />
            );

            return actions;
          },
        },
      ]}
      dataQuery={SUBMISSION_ASSOCIATIONS_QUERY}
      defaultSearch={tenantFilter}
      getItems={(data) => data.submissionVirtualEvents.items}
      getCount={(data) => data.submissionVirtualEvents.count}
      additionalFilterColumns={additionalFilterColumns}
      excludeFilterColumns={excludeFilterColumns}
      withPadding={false}
    />
  );
};
